<template>
    <div class="fe-list">
        <header v-on-clickaway="removeKeyEvents" class="fe-list__header" @click="setKeyEvents">
            <h3 class="fe-list__title">
                Filter: <em>{{ filterName }}</em>
            </h3>

            <ul class="fe-list__filters">
                <li class="fe-list__filter">
                    <button class="fe-list__filter-button" :class="{ [STATE_CLASSES.SELECTED]: !filterNew }" @click="onFilter()">
                        <font-awesome-icon :icon="['fas', 'bars']" />
                    </button>
                </li>

                <li
                    v-for="trigger of allowedTriggers"
                    :key="trigger.name"
                    class="fe-list__filter"
                    @click="onFilter(trigger.name)"
                >
                    <button
                        class="fe-list__filter-button"
                        :class="{ [STATE_CLASSES.SELECTED]: filterNew === trigger.name }"
                        :title="trigger.label"
                    >
                        <font-awesome-icon :icon="[trigger.iconSet, trigger.icon]" />
                    </button>
                </li>
            </ul>
        </header>

        <div class="fe-list__content">
            <div v-for="trigger of filteredTriggers" :key="trigger[1].name" class="fe-list__group">
                <draggable v-model="flexEventGroupNew[trigger[1].name]" @change="onReorder">
                    <transition-group class="fe-list__items" tag="ul" name="fe-transition">
                        <li v-for="flexEvent in flexEventGroupNew[trigger[1].name]" :key="flexEvent.id" class="fe-list__item">
                            <div class="fe-list__event">
                                <section class="fe-list__section">
                                    <span class="fe-list__label">Title</span>
                                    <span class="fe-list__value">{{ flexEvent.label }}</span>
                                </section>

                                <section class="fe-list__section">
                                    <button class="fe-list__action" type="button" @click="onSelect(flexEvent)">
                                        <font-awesome-icon :icon="['fas', 'edit']" />
                                    </button>

                                    <button class="fe-list__action" type="button" @click="onDelete(flexEvent)">
                                        <font-awesome-icon :icon="['fas', 'trash']" />
                                    </button>
                                </section>
                            </div>
                        </li>
                    </transition-group>
                </draggable>
            </div>

            <div v-if="isEmpty" class="fe-list__empty">
                <span class="fe-list__empty-text">No events found.</span>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import Draggable from 'vuedraggable';
import { mixin as clickaway } from 'vue-clickaway';

import triggers, { triggerNames } from '@/data/events/triggers';
import getTriggerMapping from '@/data/events/trigger-mappings';
import actions from '@/data/events/actions';
import { STATE_CLASSES } from '@/data/constants/classes';
import { FlexEventGroup } from '@/data/data-models';

const CUSTOM_ATTR_NAME = 'flex-event-group';

export default {
    name: 'flex-event-list',
    components: { Draggable },
    mixins: [clickaway],

    props: {
        // whenever a flex event is added/updated, the filter is passed here as a prop
        filter: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            actions,
            keyEventsSet: false,
            filterNew: '',
            flexEventGroupNew: null,
            STATE_CLASSES,
        };
    },

    computed: {
        ...mapState({
            selectedElements: state => state.page.selectedElements,
        }),

        filterName() {
            return this.filterNew ? triggers.get(this.filterNew).label : 'All';
        },

        allowedTriggers() {
            if (!this.selectedElements.length) return [];

            const { elementType } = this.selectedElements[0];
            const allowed = [];

            for (const [, trigger] of triggers) {
                if (getTriggerMapping(elementType).includes(trigger.name)) {
                    allowed.push(trigger);
                }
            }

            return allowed;
        },

        filteredTriggers() {
            const triggersNew = new Map(triggers);

            for (const trigger of triggers) {
                if (!this.isVisible(trigger)) triggersNew.delete(trigger[0]);
            }

            return triggersNew;
        },

        flexEventGroup() {
            return this.selectedElements[0] && this.selectedElements[0].hasElementData(CUSTOM_ATTR_NAME)
                ? this.selectedElements[0].getElementData(CUSTOM_ATTR_NAME)
                : new FlexEventGroup();
        },

        flexEventGroupItems() {
            return Object.values(this.flexEventGroup).flat();
        },

        filters() {
            // the empty string represents the "all" option
            return [''].concat(Object.values(triggerNames));
        },

        isEmpty() {
            return this.filterNew
                ? !this.flexEventGroupNew[this.filterNew].length
                : !this.flexEventGroupItems.length;
        },
    },

    watch: {
        filter() {
            // we don't want to update the filter if the user is viewing all (i.e. no filter)
            if (!this.filterNew) return;

            this.filterNew = this.filter;
        },

        flexEventGroup() {
            this.flexEventGroupNew = this.flexEventGroup;
        },
    },

    created() {
        this.flexEventGroupNew = this.flexEventGroup;
    },

    methods: {
        onSelect(flexEvent) {
            this.$emit('select', flexEvent);
        },

        onDelete(flexEvent) {
            this.$emit('delete', flexEvent);
        },

        onReorder() {
            this.selectedElements[0].setElementData(CUSTOM_ATTR_NAME, this.flexEventGroupNew);
        },

        onFilter(triggerName) {
            this.filterNew = triggerName;
        },

        setPreviousFilter() {
            const currentIndex = this.filters.indexOf(this.filterNew);
            const lastIndex = this.filters.length - 1;

            if (currentIndex - 1 >= 0) {
                this.filterNew = this.filters[currentIndex - 1];
            } else {
                this.filterNew = this.filters[lastIndex];
            }
        },

        setNextFilter() {
            const currentIndex = this.filters.indexOf(this.filterNew);
            const lastIndex = this.filters.length - 1;

            if (currentIndex + 1 <= lastIndex) {
                this.filterNew = this.filters[currentIndex + 1];
            } else {
                this.filterNew = this.filters[0];
            }
        },

        setKeyEvents() {
            if (this.keyEventsSet) return;

            this.$keydown('arrowleft', this.setPreviousFilter);
            this.$keydown('arrowright', this.setNextFilter);
            this.keyEventsSet = true;
        },

        removeKeyEvents() {
            this.$keydown();
            this.keyEventsSet = false;
        },

        isVisible(trigger) {
            const hasEvents = this.flexEventGroupNew[trigger[1].name] && this.flexEventGroupNew[trigger[1].name].length;
            const matchingFilter = !this.filterNew || this.filterNew === trigger[1].name;

            return hasEvents && matchingFilter;
        },
    },
};
</script>

<style lang="scss" scoped>
.fe-transition-move {
    transition: transform 0.5s;
}

.fe-list {
    $this: &;
    max-height: 100%;
    overflow: auto;

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1em 2rem 1rem;
        border-bottom: 1px solid var(--modal-borderColor);
    }

    &__title {
        font-weight: 500;
        color: darkgray;

        em {
            color: white;
            font-style: normal;
            font-weight: 600;
        }
    }

    &__filters {
        display: flex;
    }

    &__filter-button {
        padding: 0.5em 0.75em;

        &:hover,
        &.#{ $is-selected } {
            color: var(--modal-linkColor);
        }
    }

    &__content {
        overflow: auto;
        max-height: calc(100vh - #{ $drawer-header-height } - #{ $event-filters-height });
        padding-bottom: 2rem;
    }

    &__group {

        &:not(:first-of-type) {
            border-top: 25px solid var(--modal-borderColor);
        }
    }

    &__item {
        padding: 1.75rem 2rem;
        background-color: var(--modal-hoverBackgroundColor);

        &:not(:first-of-type) {
            border-top: 1px solid var(--modal-borderColor);
        }

        &:hover {
            cursor: grab;
        }

        &:active {
            cursor: grabbing;
        }
    }

    &__event {
        display: flex;
        align-items: center;
    }

    &__section {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        width: 28%;
        padding-right: 2em;

        &:last-of-type {
            width: 16%;
            flex-direction: row;
            justify-content: flex-end;
            padding-right: 0;
        }
    }

    &__label {
        display: block;
        font-size: 0.75em;
        color: var(--form-labelColor);
        margin-bottom: 0.25em;
    }

    &__action {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 2.5em;
        height: 2.5em;
        border-radius: 50%;
        background-color: var(--form-buttonBackgroundColor);
        color: var(--form-buttonColor);
        border: 1px solid var(--form-buttonBorderColor);

        &:hover {
            background-color: var(--form-buttonHoverBackgroundColor);
            color: var(--form-buttonHoverColor);
            border-color: var(--form-buttonHoverBorderColor);
        }

        &:not(:first-of-type) {
            margin-left: 1.5em;
        }
    }

    &__empty {
        display: flex;
        align-items: center;
        padding: 2rem;
        text-align: center;
        color: var(--form-labelColor);
    }
}
</style>
